/**
 * Lock-screen friendly nudges for Peppy AI.
 *
 * Browser limits (especially iOS Safari):
 * - TTS and Web Audio usually do not run while the screen is locked.
 * - Vibration is unavailable on iOS Safari.
 * - System Notifications (with sound) are the reliable lock-screen channel on Android
 *   and on iOS 16.4+ for installed PWAs with push permission granted.
 */

export interface CaptainNudgeOptions {
  title: string;
  body: string;
  speakText?: string;
  voiceEnabled?: boolean;
  speak?: (text: string) => void;
  /** Stable id to collapse duplicate notifications */
  tag?: string;
}

const VIBRATE_PATTERN = [200, 100, 200, 100, 200];

let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctx = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return null;
  if (!audioContext) {
    audioContext = new Ctx();
  }
  return audioContext;
}

export function playCaptainChime(): void {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      void ctx.resume();
    }

    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, ctx.currentTime);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.36);
  } catch {
    // Autoplay / background restrictions — ignore
  }
}

export function vibrateCaptain(pattern: number | number[] = VIBRATE_PATTERN): void {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
  try {
    navigator.vibrate(pattern);
  } catch {
    // iOS Safari has no vibration API
  }
}

export async function requestCaptainAlertPermission(): Promise<boolean> {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return false;
  }
  if (Notification.permission === 'granted') {
    return true;
  }
  if (Notification.permission === 'denied') {
    return false;
  }
  try {
    const result = await Notification.requestPermission();
    return result === 'granted';
  } catch {
    return false;
  }
}

export function showCaptainLockNotification(options: CaptainNudgeOptions): void {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;

  try {
    const notification = new Notification(options.title, {
      body: options.body,
      tag: options.tag || 'peppar-captain',
      vibrate: VIBRATE_PATTERN,
      silent: false,
      requireInteraction: false,
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch {
    // Some embedded browsers block Notification constructor
  }
}

export function nudgeCaptainAlert(options: CaptainNudgeOptions): void {
  const { title, body, speakText, voiceEnabled, speak, tag } = options;
  const hidden = typeof document !== 'undefined' && document.hidden;

  vibrateCaptain();
  playCaptainChime();

  if (hidden) {
    showCaptainLockNotification({ title, body, tag });
  }

  if (voiceEnabled && speakText && speak) {
    speak(speakText);
  }
}

export function primeCaptainAudioOnGesture(): void {
  playCaptainChime();
  const ctx = getAudioContext();
  if (ctx?.state === 'suspended') {
    void ctx.resume();
  }
}
